import SearchableSelect from '../../../components/SearchableSelect'
import { COUNTRIES, resolveCountryText } from '../../../lib/countries'
import { addressMappingHints } from '../../../lib/addressHints'

// A3/Z52: manual address-mapping panel shown when the AI-parsed address is about to become a
// brand new customer_addresses row (new customer, or "use BL address" on a mismatch) -- staff
// get one chance to fix AI mis-splits (e.g. Chinese province landing in `city`) before they're
// ever written, instead of only discovering it later via a broken BoxTop sync.

export interface AddressMappingFields {
  address_line_1: string
  address_line_2: string
  address_line_3: string
  address_line_4: string
  city: string
  county: string
  postcode: string
  country: string // ISO 3166-1 alpha-2, '' until staff pick one
}

export function emptyAddressMappingFields(): AddressMappingFields {
  return { address_line_1: '', address_line_2: '', address_line_3: '', address_line_4: '', city: '', county: '', postcode: '', country: '' }
}

export function resolveCountryCode(freeText?: string | null): string {
  return resolveCountryText(freeText).code ?? ''
}

const UK_REGION_WORDS = new Set(['ENGLAND', 'SCOTLAND', 'WALES', 'NORTHERN IRELAND', 'GREAT BRITAIN', 'UK', 'UNITED KINGDOM'])
const UK_POSTCODE_RE = /\b([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\b/i
const CN_PROVINCE_WORDS = new Set([
  'GUANGDONG', 'ZHEJIANG', 'JIANGSU', 'SHANDONG', 'FUJIAN', 'HEBEI', 'HENAN', 'HUBEI',
  'HUNAN', 'SICHUAN', 'ANHUI', 'JIANGXI', 'LIAONING', 'SHAANXI', 'SHANXI', 'GANSU',
  'GUIZHOU', 'YUNNAN', 'HAINAN', 'QINGHAI', 'JILIN', 'HEILONGJIANG',
])
function isProvinceSegment(seg: string): boolean {
  const upper = seg.toUpperCase().trim()
  if (/\bPROVINCE\b/.test(upper)) return true
  return CN_PROVINCE_WORDS.has(upper.replace(/\s+PROVINCE$/, '').trim())
}
const HK_COUNTRY_RE = /^(HONG\s*KONG|HKSAR|HK)$/i

// Client-side mirror of create-autojobs' parseAddress() (Z51) -- used only to pre-fill this
// panel so the happy path is a single Confirm click; the backend re-derives authoritatively
// from whatever staff leave in these fields (or re-parses server-side if left untouched).
export function quickParseAddress(raw?: string | null): AddressMappingFields {
  const empty = emptyAddressMappingFields()
  if (!raw) return empty

  let parts = raw.split('\n').map(l => l.trim()).filter(Boolean)
  if (parts.length <= 1) parts = raw.split(',').map(l => l.trim()).filter(Boolean)
  if (parts.length === 0) return empty

  let postcode = ''
  for (let i = 0; i < parts.length; i++) {
    const m = parts[i].match(UK_POSTCODE_RE)
    if (m) {
      postcode = m[1].toUpperCase().replace(/\s+/g, ' ').trim()
      const remainder = parts[i].replace(m[0], '').trim()
      if (remainder) parts[i] = remainder
      else parts.splice(i, 1)
      break
    }
  }

  const countryText = parts.length > 0 ? parts[parts.length - 1] : ''
  const withoutCountry = parts.length > 1 ? parts.slice(0, -1) : parts.slice()
  while (withoutCountry.length > 1 && UK_REGION_WORDS.has(withoutCountry[withoutCountry.length - 1].toUpperCase())) {
    withoutCountry.pop()
  }

  let county = ''
  if (withoutCountry.length > 1 && isProvinceSegment(withoutCountry[withoutCountry.length - 1])) {
    county = withoutCountry.pop() || ''
  }

  let city = ''
  let streetParts = withoutCountry
  if (withoutCountry.length > 1) {
    city = withoutCountry[withoutCountry.length - 1]
    streetParts = withoutCountry.slice(0, -1)
  }

  if (countryText && HK_COUNTRY_RE.test(countryText.trim()) && city && !HK_COUNTRY_RE.test(city.trim())) {
    streetParts = [...streetParts, city]
    city = 'HONG KONG'
  }

  return {
    address_line_1: streetParts[0] || '',
    address_line_2: streetParts[1] || '',
    address_line_3: streetParts.length > 2 ? streetParts.slice(2).join(', ') : '',
    address_line_4: '',
    city,
    county,
    postcode,
    country: resolveCountryCode(countryText),
  }
}

export default function AddressMappingPanel({
  rawText,
  fields,
  onChange,
}: {
  rawText?: string | null
  fields: AddressMappingFields
  onChange: (fields: AddressMappingFields) => void
}) {
  const set = (key: keyof AddressMappingFields) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...fields, [key]: e.target.value })

  const countryOptions = COUNTRIES.map(c => ({ id: c.code, label: c.label }))

  return (
    <div className="mt-3 grid grid-cols-1 gap-3 border border-amber-300 dark:border-amber-700 rounded-lg p-3 bg-amber-50/50 dark:bg-amber-900/10">
      <div>
        <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">AI-extracted address (raw)</p>
        <pre className="text-xs whitespace-pre-wrap break-words bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-2 text-gray-700 dark:text-gray-300 min-h-[7rem]">
          {rawText || '(no raw address text)'}
        </pre>
      </div>
      <div className="space-y-2">
        <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Confirm mapped fields</p>
        {([
          ['address_line_1', 'Address 1'],
          ['address_line_2', 'Address 2'],
          ['address_line_3', 'Address 3'],
          ['address_line_4', 'Address 4'],
          ['city', 'City'],
          ['county', 'County / Province'],
          ['postcode', 'Postcode'],
        ] as const).map(([key, label]) => (
          <div key={key} className="flex items-center gap-2">
            <label className="w-28 shrink-0 text-xs text-gray-600 dark:text-gray-400">{label}</label>
            <input
              type="text"
              value={fields[key]}
              onChange={set(key)}
              className="min-w-0 flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        ))}
        <div className="flex items-center gap-2">
          <label className="w-28 shrink-0 text-xs text-gray-600 dark:text-gray-400">Country *</label>
          <div className="min-w-0 flex-1">
            <SearchableSelect
              options={countryOptions}
              value={fields.country}
              onChange={id => onChange({ ...fields, country: id })}
              placeholder="Search country..."
            />
          </div>
        </div>
        {!fields.country && (
          <p className="text-xs text-red-600 dark:text-red-400">Country is required before this address can be saved.</p>
        )}
        {/* Hints only (never block): the text on the left compared with the fields. They update as the fields are edited. */}
        {addressMappingHints(rawText, fields, text => resolveCountryCode(text) || null).map(h => (
          <p key={h.id} role="status" className="text-xs text-amber-700 dark:text-amber-400">⚠ {h.message}</p>
        ))}
      </div>
    </div>
  )
}
